import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthImage from '../images/auth/codeframe.png';
import Logo from '../images/auth/logo (2).png';

const OTP = () => {
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [verificationId, setVerificationId] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [countdown, setCountdown] = useState(15);
    const [canResend, setCanResend] = useState(false);
    const navigate = useNavigate();
    const inputRefs = useRef([]);

    // Retrieve verification ID from localStorage on component mount
    useEffect(() => {
        const storedVerificationId = localStorage.getItem('quickcare-verification-id');
        if (storedVerificationId) {
            setVerificationId(storedVerificationId);
            console.log("Retrieved verification ID:", storedVerificationId);
        } else {
            navigate('/signup');
        }
    }, [navigate]);

    // Initialize timer state from localStorage or start new timer
    useEffect(() => {
        const timerKey = 'quickcare-otp-timer';
        const storedTimerData = localStorage.getItem(timerKey);
        
        if (storedTimerData) {
            try {
                const { startTime, duration } = JSON.parse(storedTimerData);
                const elapsed = Math.floor((Date.now() - startTime) / 1000);
                const remaining = Math.max(0, duration - elapsed);
                
                if (remaining > 0) {
                    setCountdown(remaining);
                    setCanResend(false);
                } else {
                    setCountdown(0);
                    setCanResend(true);
                }
            } catch (error) {
                console.error('Error parsing timer data:', error);
                // Fallback to default timer
                setCountdown(15);
                setCanResend(false);
                localStorage.setItem(timerKey, JSON.stringify({
                    startTime: Date.now(),
                    duration: 15
                }));
            }
        } else {
            // Start new timer
            setCountdown(15);
            setCanResend(false);
            localStorage.setItem(timerKey, JSON.stringify({
                startTime: Date.now(),
                duration: 15
            }));
        }
    }, []);

    // Countdown timer for resend OTP
    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        } else {
            setCanResend(true);
            // Clear timer data when countdown reaches 0
            localStorage.removeItem('quickcare-otp-timer');
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    const handleChange = (index, value) => {
        // Only allow digits
        if (/^\d*$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            // Auto focus to next input if a digit is entered
            if (value && index < 5) {
                inputRefs.current[index + 1].focus();
            }
        }
    };

    const handleKeyDown = (index, e) => {
        // Handle backspace to move to previous input
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        const otpCode = otp.join('');
        if (otpCode.length !== 6) {
            setError('Please enter a complete 6-digit OTP');
            setIsSubmitting(false);
            return;
        }

        try {
            const myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");
            myHeaders.append("Accept", "application/json");

            const raw = JSON.stringify({
                verification_id: verificationId,
                verification_code: otpCode
            });

            const requestOptions = {
                method: "POST",
                headers: myHeaders,
                body: raw,
                redirect: "follow"
            };

            const response = await fetch("https://devapi.quickcare.ng/api/v1/user/signup/verify", requestOptions);
            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || 'OTP verification failed');
            }

            console.log("OTP verification successful:", result);
            
            // Save the access token to localStorage
            if (result.data?.access_token) {
                localStorage.setItem('token', result.data.access_token);
                console.log("Access token saved to localStorage");
            }
            
            // Wait 1 second before redirecting to home page
            setTimeout(() => {
                navigate('/');
            }, 1000); 
            
        } catch (error) {
            console.error("OTP verification error:", error);
            setError(error.message || 'OTP verification failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResendOTP = () => {
        // Reset countdown and disable resend button
        setCountdown(30);
        setCanResend(false);
        
        // Update timer data in localStorage
        const timerKey = 'quickcare-otp-timer';
        localStorage.setItem(timerKey, JSON.stringify({
            startTime: Date.now(),
            duration: 30
        }));
        
        // Here you would call your API to resend OTP
        console.log("Resending OTP to verification ID:", verificationId);
    };

    // Check if all 6 digits are entered
    const isOtpComplete = otp.join('').length === 6;

    return (
        <div className="min-h-screen flex flex-col md:flex-row bg-white instrument-sans-normal">
            {/* Left side - Form */}
            <div className="w-full md:w-1/2 flex items-center justify-center p-4 md:p-8 lg:p-12">
                <div className="w-full max-w-md space-y-8">
                    <div className="">
                        <img src={Logo} alt="Company Logo" className='w-[196px] h-[49px]'/>
                        <h2 className="mt-6 text-2xl instrument-sans-semibold text-gray-700">
                            Verify OTP
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                            Enter the 6-digit code sent to your phone number
                        </p>
                    </div>
                    
                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                            <span className="block sm:inline">{error}</span>
                        </div>
                    )}
                    
                    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                        <div className="flex justify-between space-x-1">
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={(el) => (inputRefs.current[index] = el)}
                                    type="text"
                                    maxLength="1"
                                    value={digit}
                                    onChange={(e) => handleChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    className="w-[68px] h-[44px] outline-0 text-center border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-lg"
                                    required
                                    disabled={isSubmitting}
                                />
                            ))}
                        </div>

                        <div className="text-sm">
                            <p className="text-[#8B8D8C]">
                                Did not receive the OTP?{' '}
                                {canResend ? (
                                    <a href="/verify-signup-channel"  
                                        type="button" 
                                        className="text-[#45B139] hover:underline focus:outline-none"
                                        onClick={handleResendOTP}
                                        disabled={isSubmitting}
                                    >
                                         Resend OTP 
                                    </a>
                                ) : (
                                    <span className="text-black font-semibold">
                                        Resend OTP in 
                                        <span className='text-[#45B139] ml-1'>
                                            {countdown}s
                                        </span>
                                    </span>
                                )}
                            </p>
                        </div>
                        
                        <div>
                            <button
                                type="submit"
                                className={`w-full flex justify-center py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#069125] hover:bg-[#057a1f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${!isOtpComplete ? 'opacity-20 cursor-not-allowed' : ''}`}
                                disabled={!isOtpComplete || isSubmitting}
                            >
                                {isSubmitting ? 'Verifying...' : 'Verify OTP'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Right side - Background Image (hidden on mobile) */}
            <div 
                className="hidden md:block md:w-1/2 bg-indigo-50 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${AuthImage})` }}
            >
                {/* Optional: You can add content over the background image here if needed */}
            </div>
        </div>
    );
};

export default OTP;